import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface DeleteCascadeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  type: "log" | "story" | "event" | "rundown";
  name: string;
  counts: { logs: number; stories: number; events: number; segments: number } | null;
  isLoading: boolean;
  isDeleting: boolean;
}

const typeLabels: Record<string, string> = {
  log: "Log",
  story: "Story",
  event: "Event",
  rundown: "Rundown",
};

const DeleteCascadeModal: React.FC<DeleteCascadeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  type,
  name,
  counts,
  isLoading,
  isDeleting,
}) => {
  const { t } = useTranslation();

  const totalItems = counts
    ? (counts.events || 0) + (counts.stories || 0) + (counts.logs || 0) + (counts.segments || 0)
    : 0;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        data-testid="delete-cascade-modal"
        showCloseButton={false}
        className="sm:max-w-[440px] border-red-500/30 bg-gradient-to-b from-[#1e1e1e] to-[#141414] shadow-[0_20px_60px_rgba(0,0,0,0.6),0_0_30px_rgba(255,60,60,0.1)]"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[10px] bg-red-500/15 flex items-center justify-center">
              <i className="bx bx-trash text-xl text-[#ff4444]" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">
                Delete {typeLabels[type] || type}
              </DialogTitle>
              <DialogDescription className="text-xs text-[#aaa] max-w-[280px] truncate">
                {name}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Counts */}
        {isLoading ? (
          <div className="py-5 text-center text-[#888]">
            <i className="bx bx-loader-alt bx-spin text-2xl" />
            <div className="mt-2 text-xs">{t("logger.deleteCascade.calculatingImpact")}</div>
          </div>
        ) : counts ? (
          <div className="bg-red-500/[0.06] rounded-lg p-3.5 border border-red-500/[0.12]">
            <div className="text-[0.75rem] text-[#ff6666] font-bold mb-2.5 uppercase tracking-wide">
              {t("logger.deleteCascade.willBeDeleted")}
            </div>
            <div className="flex flex-col gap-1.5">
              {type === "rundown" && counts.events > 0 && (
                <CountRow
                  icon="bx-calendar-event"
                  label={t("logger.deleteCascade.events")}
                  count={counts.events}
                />
              )}
              {counts.stories > 0 && (
                <CountRow
                  icon="bx-file"
                  label={t("logger.deleteCascade.stories")}
                  count={counts.stories}
                />
              )}
              {counts.logs > 0 && (
                <CountRow
                  icon="bx-list-ul"
                  label={t("logger.deleteCascade.logs")}
                  count={counts.logs}
                />
              )}
              {counts.segments > 0 && (
                <CountRow
                  icon="bx-microphone"
                  label={t("logger.deleteCascade.transcripts")}
                  count={counts.segments}
                />
              )}
              {totalItems === 0 && type === "log" && (
                <CountRow icon="bx-list-ul" label={t("logger.deleteCascade.log")} count={1} />
              )}
            </div>
          </div>
        ) : null}

        {/* Warning */}
        <div className="text-xs text-[#999] leading-relaxed italic">
          {t("logger.deleteCascade.cannotUndo")}
          {type !== "log" && ` ${t("logger.deleteCascade.storiesMarkedDeleted")}`}
        </div>

        {/* Actions */}
        <DialogFooter className="bg-transparent border-t-0 mx-0 mb-0 p-0 flex-row justify-end">
          <Button
            data-testid="delete-cascade-cancel"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={isDeleting}
          >
            {t("logger.deleteCascade.cancel")}
          </Button>
          <Button
            data-testid="delete-cascade-confirm"
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            disabled={isLoading || isDeleting}
          >
            {isDeleting ? (
              <>
                <i className="bx bx-loader-alt bx-spin" /> {t("logger.deleteCascade.deleting")}
              </>
            ) : (
              <>
                <i className="bx bx-trash" /> {t("logger.deleteCascade.delete")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CountRow: React.FC<{ icon: string; label: string; count: number }> = ({
  icon,
  label,
  count,
}) => (
  <div className="flex items-center gap-2 text-sm">
    <i className={`bx ${icon} text-[#ff6666] text-sm`} />
    <span className="text-[#ddd]">{count}</span>
    <span className="text-[#888]">{label}</span>
  </div>
);

export default DeleteCascadeModal;
